import { forwardRef, useState } from "react";
import { Linking, Platform, Share, StyleSheet } from "react-native";
import type { SheetAdapterRef } from "react-native-bottom-sheet-stack";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LPressable from "@components/LPressable";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import {
  BottomSheetSubtitle,
  BottomSheetTopBarLayout,
} from "@utils/swmModal/components/BottomSheetPrimitives";
import type { Collect } from "@libs/platform-wire";
import { useDataAuth, useRuntime } from "../../../../../runtime/provider";
import {
  VENDOR_TICKETS_EVENTS,
  createVendorTicketsHelpModel,
  vendorAppStoreUrl,
  vendorLoginButtonLabel,
  type VendorOpenAction,
} from "@libs/shared-ui";

type Login = {
  userId: string;
  email: string;
  password: string;
  attemptId?: string;
};

/** My Tickets' analytics beside each action's name: the purchase's listing,
 *  never the seller. */
type Analytics = { listing_id: string | null };

/**
 * My Tickets' "?" for a purchase bought on a seller's site, as the read's
 * `collect` says it, through `createVendorTicketsHelpModel`, which Link in
 * Bio's tickets page shares: how these tickets work, what was bought (the
 * order's lines and the seller's references), the owner's ticket documents
 * (a PDF to download, the seller's ticket page) and, for an order Lissen
 * holds in a seller account, the owner's account box; otherwise "Open …",
 * the seller's app or site. Kept out of My Tickets, which shows only the
 * tickets. The password is fetched only when the owner asks (ADR 0006), and
 * never shown to anyone signed in as someone else.
 */
export const VendorTicketsHelpSheet = forwardRef<
  SheetAdapterRef,
  { accessId: string; collect: Collect }
>(function VendorTicketsHelpSheet({ accessId, collect }, ref) {
  return (
    <AppModalSheet ref={ref}>
      <VendorTicketsHelp accessId={accessId} collect={collect} />
    </AppModalSheet>
  );
});

export function VendorTicketsHelp({
  accessId,
  collect,
}: {
  accessId: string;
  collect: Collect;
}) {
  const help = createVendorTicketsHelpModel(collect);
  const analytics: Analytics = { listing_id: collect.listingId };
  const [selectedOrder, setSelectedOrder] = useState<string | undefined>();
  const attemptId =
    help.account?.orders.find((order) => order.attemptId === selectedOrder)
      ?.attemptId ?? help.account?.orders[0]?.attemptId;
  const buyer = useDataAuth().firebaseUser;
  const { sellerLogin, auth } = useRuntime().ports;
  const [login, setLogin] = useState<Login | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const shown =
    login?.userId === buyer?.uid && login?.attemptId === attemptId
      ? login
      : null;

  async function reveal() {
    if (shown) {
      setLogin(null);
      return;
    }
    if (loading || !buyer || auth.currentUser() !== buyer) return;
    setLoading(true);
    setFailed(false);
    try {
      const details = await sellerLogin(buyer, auth.currentUser).login(accessId, attemptId);
      setLogin({ userId: buyer.uid, ...details, attemptId });
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <BottomSheetTopBarLayout title={help.title} contentProps={{ gap: "m1" }}>
      <BottomSheetSubtitle>{help.subtitle}</BottomSheetSubtitle>

      <LBox style={styles.card} gap="s1" testID="vendor-tickets-order">
        <LText variant="Caption/C1/Medium" color="blackTranslucent70">
          {help.order.heading}
        </LText>
        {help.order.lines.map((line) => (
          <LText key={line.key} variant="Body/B1/Medium" color="black">
            {line.text}
          </LText>
        ))}
        {help.order.references.map((reference) => (
          <LText
            key={reference.key}
            variant="Body/B2/Medium"
            color="blackTranslucent70"
          >
            {reference.label}{" "}
            <LText variant="Body/B2/Medium" color="black" selectable>
              {reference.value}
            </LText>
          </LText>
        ))}
      </LBox>

      {help.documents ? (
        <LBox style={styles.card} gap="s2" testID="vendor-tickets-documents">
          <LText variant="Caption/C1/Medium" color="blackTranslucent70">
            {help.documents.heading}
          </LText>
          <LBox flexDirection="row" gap="s2">
            {help.documents.items.map((document) => (
              <LPressable
                key={document.key}
                accessibilityRole="button"
                onPress={() => void Linking.openURL(document.url)}
                sentryEventName={document.eventName}
                analytics={analytics}
                style={styles.button}
              >
                <LText variant="Body/B2/Medium" color="black">
                  {document.label}
                </LText>
              </LPressable>
            ))}
          </LBox>
        </LBox>
      ) : null}

      {help.account ? (
        <LBox style={styles.card} gap="s2" testID="vendor-tickets-account">
          <LText variant="Caption/C1/Medium" color="blackTranslucent70">
            {help.account.heading}
          </LText>
          <LText variant="Body/B2/Medium" color="black">
            {help.account.body}
          </LText>
          {help.account.orders.length > 1 ? (
            <LBox gap="s2">
              {help.account.orders.map((order) => (
                <LPressable
                  key={order.attemptId}
                  accessibilityRole="button"
                  accessibilityLabel={order.label}
                  sentryEventName={VENDOR_TICKETS_EVENTS.selectOrder}
                  analytics={analytics}
                  style={{ ...styles.button, flex: 0 }}
                  accessibilityState={{
                    selected: order.attemptId === attemptId,
                    disabled: loading,
                  }}
                  disabled={loading}
                  onPress={() => {
                    setSelectedOrder(order.attemptId);
                    setLogin(null);
                    setFailed(false);
                  }}
                >
                  <LText variant="Body/B2/Medium" color="black">
                    {order.attemptId === attemptId ? "✓ " : ""}
                    {order.label}
                  </LText>
                </LPressable>
              ))}
            </LBox>
          ) : null}
          {shown ? (
            <LBox gap="s2">
              <LoginRow
                label="Email"
                value={shown.email}
                eventName={VENDOR_TICKETS_EVENTS.copyEmail}
                analytics={analytics}
              />
              {help.account.showsPassword ? (
                <LoginRow
                  label="Password"
                  value={shown.password}
                  eventName={VENDOR_TICKETS_EVENTS.copyPassword}
                  analytics={analytics}
                />
              ) : null}
            </LBox>
          ) : null}
          {failed ? (
            <LText variant="Body/B2/Medium" color="blackTranslucent70">
              {help.account.failed}
            </LText>
          ) : null}
          <LBox flexDirection="row" gap="s2">
            <LPressable
              accessibilityRole="button"
              disabled={loading}
              onPress={() => void reveal()}
              sentryEventName={VENDOR_TICKETS_EVENTS.revealLogin}
              analytics={analytics}
              style={styles.button}
            >
              <LText variant="Body/B2/Medium" color="black">
                {vendorLoginButtonLabel(!!shown, loading)}
              </LText>
            </LPressable>
            {help.account.open ? (
              <OpenVendor action={help.account.open} analytics={analytics} />
            ) : null}
          </LBox>
        </LBox>
      ) : help.open ? (
        <LBox flexDirection="row" gap="s2">
          <OpenVendor action={help.open} analytics={analytics} />
        </LBox>
      ) : null}
    </BottomSheetTopBarLayout>
  );
}

/** "Open …": the seller's (or its ticket app's) app, or a site. */
function OpenVendor({
  action,
  analytics,
}: {
  action: VendorOpenAction;
  analytics: Analytics;
}) {
  return (
    <LPressable
      accessibilityRole="button"
      onPress={() => void openAction(action)}
      sentryEventName={VENDOR_TICKETS_EVENTS.open}
      analytics={analytics}
      style={styles.button}
    >
      <LText variant="Body/B2/Medium" color="black">
        {action.label}
      </LText>
    </LPressable>
  );
}

function LoginRow({
  label,
  value,
  eventName,
  analytics,
}: {
  label: string;
  value: string;
  eventName: string;
  analytics: Analytics;
}) {
  return (
    <LBox flexDirection="row" alignItems="center" gap="s2" style={styles.row}>
      <LBox flex={1} minWidth={0} gap="s1">
        <LText variant="Caption/C1/Medium" color="blackTranslucent70">
          {label}
        </LText>
        {/* Whole, wrapping if it must: it may be typed by hand. */}
        <LText variant="Body/B1/Medium" color="black" selectable>
          {value}
        </LText>
      </LBox>
      <LPressable
        accessibilityRole="button"
        accessibilityLabel={`Copy ${label.toLowerCase()}`}
        onPress={() => void copy(value)}
        sentryEventName={eventName}
        analytics={analytics}
        style={styles.copy}
      >
        <LText variant="Caption/C1/Medium" color="black">
          Copy
        </LText>
      </LPressable>
    </LBox>
  );
}

/** Opens "Open …": an app by its scheme where it has one, else its store
 *  page; a site as it is. */
async function openAction(action: VendorOpenAction) {
  switch (action.kind) {
    case "app":
      return openApp(action);
    case "site":
      return Linking.openURL(action.url);
  }
}

/** An app: without the app installed, its store page is where its tickets
 *  are reached. */
async function openApp(app: Extract<VendorOpenAction, { kind: "app" }>) {
  const store = vendorAppStoreUrl(
    app,
    Platform.OS === "android"
      ? "Android"
      : Platform.OS === "web" && typeof navigator !== "undefined"
        ? navigator.userAgent
        : "",
  );
  if (Platform.OS === "web") {
    await Linking.openURL(store);
    return;
  }
  // The app if it is installed, else its store page. Opened directly, not
  // checked first: asking iOS whether it can open a scheme needs it
  // declared in the app's Info.plist, a native change that would stop this
  // shipping over the air.
  if (app.scheme) await Linking.openURL(app.scheme).catch(() => Linking.openURL(store));
  else await Linking.openURL(store);
}

async function copy(value: string) {
  if (
    Platform.OS === "web" &&
    typeof navigator !== "undefined" &&
    navigator.clipboard
  ) {
    await navigator.clipboard.writeText(value);
    return;
  }
  // The share sheet's own Copy: no credential in another app's cache or in
  // our logs.
  await Share.share({ message: value });
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.6)",
    borderRadius: 16,
    padding: 16,
  },
  button: {
    alignItems: "center",
    backgroundColor: "rgba(17, 17, 23, 0.08)",
    borderRadius: 999,
    flex: 1,
    height: 40,
    justifyContent: "center",
  },
  row: {
    backgroundColor: "rgba(17, 17, 23, 0.05)",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  copy: {
    backgroundColor: "rgba(17, 17, 23, 0.08)",
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
});
